import type { Dimensions, KeyCount, RunRow, RunSummary } from '@growth/client';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { type BarItem, BarList, ColumnChart } from '../components/charts.tsx';
import { FilterBar } from '../components/FilterBar.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { Card, ChartCard, DataTable, ErrorBox, StatTile } from '../components/ui.tsx';
import { useGlobalFilters } from '../filters.ts';
import { fmtDateTime, fmtDuration, fmtInt, fmtNum, fmtPct, shortId } from '../format.ts';
import { ciText, shareNote } from '../metrics.ts';
import { useApi } from '../session.tsx';

export const RESULT_LABEL: Record<string, string> = {
  victory: 'Victoria',
  defeat: 'Derrota',
  quit: 'Salida desde el menú',
  abandoned: 'Abandonada (app cerrada)',
  timeout: 'Tiempo agotado',
  unknown: 'Desconocido',
};

export const resultLabel = (r: string | null | undefined) => (r ? (RESULT_LABEL[r] ?? r) : 'sin final');

function items(list: KeyCount<string | number>[], label: (k: string | number) => string = String): BarItem[] {
  const total = list.reduce((s, x) => s + x.n, 0);
  return list.map((x) => ({
    label: label(x.key),
    value: x.n,
    note: shareNote(x.n, total),
    tooltip: { title: label(x.key), rows: [{ label: 'runs', value: fmtInt(x.n) }, { label: 'del total', value: total ? fmtPct(x.n / total) : '—' }] },
  }));
}

function countTable(list: KeyCount<string | number>[], keyLabel: string, label: (k: string | number) => string = String) {
  const total = list.reduce((s, x) => s + x.n, 0);
  return { columns: [{ label: keyLabel }, { label: 'Runs', numeric: true }, { label: '%', numeric: true }], rows: list.map((x) => [label(x.key), fmtInt(x.n), total ? fmtPct(x.n / total) : '—']) };
}

const PAGE = 25;

function RunList({ gameId, filtersKey, params }: { gameId: string; filtersKey: string; params: ReturnType<typeof useGlobalFilters>['params'] }) {
  const [mode, setMode] = useState('');
  const [result, setResult] = useState('');
  const [offset, setOffset] = useState(0);
  const navigate = useNavigate();
  const dims = useApi<Dimensions>(`dims:${gameId}:${params.environment}`, (api, s) => api.dimensions(gameId, params.environment, s));
  const runs = useApi<RunRow[]>(`runs:${gameId}:${filtersKey}:${mode}:${result}:${offset}`, (api, s) =>
    api.runs(gameId, { ...params, mode: mode || undefined, result: result || undefined, limit: PAGE, offset }, s),
  );
  const rows = runs.data ?? [];
  const env = params.environment && params.environment !== 'production' ? `?environment=${params.environment}` : '';

  return (
    <Card
      title="Runs recientes"
      subtitle="Haz clic en una run para reconstruirla evento a evento"
      actions={
        <>
          <select className="input" aria-label="Modo" value={mode} onChange={(e) => (setMode(e.target.value), setOffset(0))}>
            <option value="">Todos los modos</option>
            {(dims.data?.run_modes ?? []).map((m) => (
              <option key={m.value} value={m.value}>
                {m.value}
              </option>
            ))}
          </select>
          <select className="input" aria-label="Resultado" value={result} onChange={(e) => (setResult(e.target.value), setOffset(0))}>
            <option value="">Todos los resultados</option>
            {Object.entries(RESULT_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </>
      }
    >
      {runs.error && <ErrorBox error={runs.error} onRetry={runs.reload} />}
      <div className={`loading-frame${runs.loading && runs.data ? ' stale' : ''}`}>
        <DataTable
          compact
          onRowClick={(i) => rows[i] && navigate(`/g/${gameId}/runs/${rows[i]!.run_id}${env}`)}
          data={{
            columns: [
              { label: 'Inicio' },
              { label: 'Run' },
              { label: 'Jugador' },
              { label: 'Modo / nivel' },
              { label: 'Resultado' },
              { label: 'Duración', numeric: true },
              { label: 'Oleada', numeric: true },
              { label: 'Muertes', numeric: true },
              { label: 'Causa' },
              { label: 'Revives', numeric: true },
              { label: 'Anuncios', numeric: true },
              { label: 'Versión' },
            ],
            rows: rows.map((r) => [
              fmtDateTime(r.started_at ?? r.ended_at, { seconds: false }),
              `#${r.run_number ?? '?'}`,
              <Link key="p" to={`/g/${gameId}/players/${r.installation_id}${env}`} onClick={(e) => e.stopPropagation()} className="mono">
                {shortId(r.installation_id)}
              </Link>,
              [r.mode, r.level_id].filter(Boolean).join(' / ') || '—',
              resultLabel(r.result),
              fmtDuration(r.duration_s),
              fmtInt(r.wave_reached),
              fmtInt(r.deaths),
              r.death_reason ?? '—',
              fmtInt(r.revives),
              fmtInt(r.ads_watched),
              r.app_version ?? '—',
            ]),
          }}
        />
        {!rows.length && !runs.loading && <div className="empty">No hay runs con estos filtros.</div>}
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
        <button type="button" className="btn small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
          ← Anteriores
        </button>
        <button type="button" className="btn small" disabled={rows.length < PAGE} onClick={() => setOffset(offset + PAGE)}>
          Siguientes →
        </button>
      </div>
    </Card>
  );
}

export function RunsPage() {
  const { gameId = '' } = useParams();
  const filters = useGlobalFilters();
  const sum = useApi<RunSummary>(`runsum:${gameId}:${filters.key}`, (api, s) => api.runSummary(gameId, filters.params, s));
  const s = sum.data;
  const stale = sum.loading && !!s;
  const churn = s?.churn_last_run;

  return (
    <>
      <PageHeader title="Runs" sub="Qué pasa dentro de las partidas: resultados, muertes, oleadas y elecciones." />
      <FilterBar filters={filters} />
      {sum.error && <ErrorBox error={sum.error} onRetry={sum.reload} />}
      {s && (
        <>
          <div className={`kpis loading-frame${stale ? ' stale' : ''}`}>
            <StatTile label="Runs iniciadas" value={fmtInt(s.runs)} sub={`${fmtPct(s.runs ? s.completed / s.runs : null)} con final registrado`} />
            <StatTile label="Duración media" value={fmtDuration(s.avg_duration_s)} />
            <StatTile label="Oleada media alcanzada" value={fmtNum(s.avg_wave_reached, 1)} />
            <StatTile label="Runs con revive" value={fmtPct(s.revive_rate.rate)} sub={ciText(s.revive_rate)} />
            <StatTile label="Revive con rewarded" value={fmtPct(s.rewarded_revive_rate.rate)} sub={ciText(s.rewarded_revive_rate)} />
            <StatTile label="Llegan al jefe" value={fmtPct(s.boss_reach_rate.rate)} sub={`lo matan: ${fmtPct(s.boss_kill_rate.rate)}`} />
            <StatTile label="Anuncios por run" value={fmtNum(s.avg_ads_per_run, 2)} />
          </div>

          <div className="grid cols-3">
            <ChartCard title="Resultado" stale={stale} table={countTable(s.results, 'Resultado', (k) => resultLabel(String(k)))}>
              <BarList ariaLabel="Runs por resultado" items={items(s.results, (k) => resultLabel(String(k)))} format={fmtInt} />
            </ChartCard>
            <ChartCard title="Causas de muerte" subtitle="Último golpe (death_reason)" stale={stale} table={countTable(s.death_reasons, 'Causa')}>
              <BarList ariaLabel="Runs por causa de muerte" items={items(s.death_reasons)} format={fmtInt} />
            </ChartCard>
            <ChartCard
              title="Modos"
              stale={stale}
              table={{
                columns: [{ label: 'Modo' }, { label: 'Runs', numeric: true }, { label: 'Victorias', numeric: true }, { label: 'Duración media', numeric: true }],
                rows: s.modes.map((m) => [m.key, fmtInt(m.n), fmtPct(m.n ? m.victories / m.n : null), fmtDuration(m.avg_duration_s)]),
              }}
            >
              <BarList
                ariaLabel="Runs por modo"
                items={s.modes.map((m) => ({
                  label: m.key,
                  value: m.n,
                  note: `${fmtPct(m.n ? m.victories / m.n : null, 0)} victorias`,
                  tooltip: { title: m.key, rows: [{ label: 'runs', value: fmtInt(m.n) }, { label: 'victorias', value: fmtInt(m.victories) }, { label: 'duración media', value: fmtDuration(m.avg_duration_s) }] },
                }))}
                format={fmtInt}
              />
            </ChartCard>
          </div>

          <ChartCard title="Oleada en la que termina la run" subtitle="Runs no ganadas: dónde se atascan los jugadores" stale={stale} table={countTable(s.wave_at_end_distribution, 'Oleada')}>
            {s.wave_at_end_distribution.length ? (
              <ColumnChart
                ariaLabel="Runs por oleada final"
                x={s.wave_at_end_distribution.map((w) => w.key)}
                formatX={(v) => `${v}`}
                formatY={fmtInt}
                labelMax
                series={[{ key: 'runs', label: 'Runs', slot: 1, values: s.wave_at_end_distribution.map((w) => w.n) }]}
              />
            ) : (
              <div className="empty">Sin datos de oleadas (wave_reached).</div>
            )}
          </ChartCard>

          <div className="grid cols-2" style={{ marginTop: 16 }}>
            <ChartCard title="Armas más usadas" subtitle="Runs en las que aparece cada arma" stale={stale} table={countTable(s.top_weapons, 'Arma')}>
              <BarList ariaLabel="Armas más usadas" items={items(s.top_weapons)} format={fmtInt} />
            </ChartCard>
            <ChartCard title="Mejoras más elegidas" stale={stale} table={countTable(s.top_upgrades, 'Mejora')}>
              <BarList ariaLabel="Mejoras más elegidas" items={items(s.top_upgrades)} format={fmtInt} />
            </ChartCard>
          </div>

          {churn && (
            <>
              <h2 className="section-title">¿Dónde se van los jugadores?</h2>
              <p className="muted small" style={{ marginTop: -4 }}>
                Última run de las instalaciones del periodo que llevan días sin jugar: {fmtInt(churn.churned)} jugadores, {fmtInt(churn.never_played)} sin ninguna run.
              </p>
              <div className="grid cols-3">
                <ChartCard title="Resultado de su última run" table={countTable(churn.last_result, 'Resultado', (k) => resultLabel(String(k)))}>
                  <BarList ariaLabel="Resultado de la última run" items={items(churn.last_result, (k) => resultLabel(String(k)))} format={fmtInt} />
                </ChartCard>
                <ChartCard title="Qué les mató" table={countTable(churn.last_death_reason, 'Causa')}>
                  <BarList ariaLabel="Causa de muerte en la última run" items={items(churn.last_death_reason)} format={fmtInt} />
                </ChartCard>
                <ChartCard title="Número de run en la que se fueron" table={countTable(churn.last_run_number, 'Run')}>
                  {churn.last_run_number.length ? (
                    <ColumnChart
                      ariaLabel="Número de la última run"
                      x={churn.last_run_number.map((x) => x.key)}
                      formatX={(v) => `#${v}`}
                      formatY={fmtInt}
                      series={[{ key: 'n', label: 'Jugadores', slot: 1, values: churn.last_run_number.map((x) => x.n) }]}
                    />
                  ) : (
                    <div className="empty">Sin datos.</div>
                  )}
                </ChartCard>
              </div>
            </>
          )}
        </>
      )}
      <div style={{ marginTop: 16 }}>
        <RunList gameId={gameId} filtersKey={filters.key} params={filters.params} />
      </div>
    </>
  );
}
